import * as React from 'react';
import { Title } from '../../../title';
import { I18n } from 'react-redux-i18n';
import { Subtitle } from '../../../subtitle';
import { SELECT_EMPTY_VALUE, Select, SelectOption, SelectValue } from '../../../select';
import { Flex } from '@common/components/Flex';
import { ConditionalField } from '../../../conditional-field';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { FolderPicker } from '../../../folder-picker';
import { Checkbox } from '../../../checkbox';
import { TextField } from '../../../text-field';
import { FieldLayout } from '../../../field';
import { EmailTemplateListDto } from '@common/dto/email-template/list';
import { QuestionnaireManagementErrors } from '@modules/fhq-design-tool/controllers/questionnaire-validation';
import { QuestionnaireUpdateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-update-local-dto';
import { QuestionnaireCreateLocalDto } from '@modules/fhq-design-tool/dto/questionnaire-create-local-dto';

import './reviewed.styles.scss';

export type ReviewedProps = {
  errors?: QuestionnaireManagementErrors
  emailTemplates: Array<EmailTemplateListDto>;
  folders: Array<FolderPrimaryListDto>;
  questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto;
  onChange: (questionnaire: QuestionnaireUpdateLocalDto | QuestionnaireCreateLocalDto) => void;
};

export const Reviewed: React.FC<ReviewedProps> = (props) => {
  const { errors, onChange, questionnaire, folders, emailTemplates } = props;

  const emailTemplatesOptions = React.useMemo((): Array<SelectOption> => {
    return emailTemplates.map((x) => ({
      value: x.id,
      label: x.name,
    })); 
  }, [emailTemplates]);

  const handleSendEmailToPatientChange = React.useCallback((val: SelectValue | null | undefined) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        sendEmailToPatient: val as EmailTemplateListDto['id'] | null | undefined
      }
    });
  }, [onChange, questionnaire]);

  const handleSendEmailToStaffChange = React.useCallback((val: SelectValue | null | undefined) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        sendEmailToStuff: val as EmailTemplateListDto['id'] | null | undefined
      }
    });
  }, [onChange, questionnaire]);

  const handleMoveToFolderChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        moveOnReviewed: val,
        reviewedTargetFolderId: val ? questionnaire.statusTab.reviewedTargetFolderId : undefined,
      }
    });
  }, [onChange, questionnaire]);

  const handleTargetFolderChange = React.useCallback((folderId: FolderPrimaryListDto['id']) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        reviewedTargetFolderId: folderId
      }
    });
  }, [onChange, questionnaire]);

  const handleTransmitPedigreePdfToEmrChange = React.useCallback((val: boolean) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        transmitToEMR: val,
      }
    });
  }, [onChange, questionnaire]);

  const handleEmailsChange = React.useCallback((val: string) => {
    onChange({
      ...questionnaire,
      statusTab: {
        ...questionnaire.statusTab,
        sendEmailToStuffEmails: val.trim(),
      }
    });
  }, [onChange, questionnaire]);

  return (
    <div className="FhqDesignToolStatusActionsReviewed">
      <Title text={I18n.t('Reviewed')} />
      <Subtitle
        text={I18n.t("Set the actions to occur when you set invite status to 'Reviewed'")}
      />
      <Select 
        clearable
        labelWidth={140}
        options={emailTemplatesOptions}
        label={I18n.t('Send email to patient')}
        value={questionnaire.statusTab.sendEmailToPatient === 0
          ? SELECT_EMPTY_VALUE
          : questionnaire.statusTab.sendEmailToPatient
        }
        onChange={handleSendEmailToPatientChange}
        metadata={{
          description: 'send-email-to-patient'
        }}
      />
      <Flex alignItems="flex-start">
        <Select 
          clearable
          labelWidth={140}
          options={emailTemplatesOptions}
          label={I18n.t('Send email to staff')}
          value={questionnaire.statusTab.sendEmailToStuff === 0
            ? SELECT_EMPTY_VALUE
            : questionnaire.statusTab.sendEmailToStuff
          }
          onChange={handleSendEmailToStaffChange}
          metadata={{
            description: 'send-email-to-staff'
          }}
        />
        <TextField 
          value={questionnaire.statusTab.sendEmailToStuffEmails ?? ''}
          errors={errors?.properties?.sendEmailToStuffEmails}
          metadata={{
            description: 'staff-emails'
          }}
          className="FhqDesignToolStatusActionsReviewed__email-field"
          placeholder="email1@example.com, email2@example.com"
          onChange={handleEmailsChange}
        />
      </Flex>
      <ConditionalField
        value={questionnaire.statusTab.moveOnReviewed}
        metadata={{
          description: 'move-to-folder'
        }}
        onChange={handleMoveToFolderChange}
        label={I18n.t('Move to Folder')}
      >
        <FolderPicker
          errors={errors?.properties?.reviewedTargetFolder}
          required={questionnaire.statusTab.moveOnReviewed}
          labelWidth={140}
          value={questionnaire.statusTab.reviewedTargetFolderId ?? undefined}
          onChange={handleTargetFolderChange}
          metadata={{
            description: 'move-to-folder-picker',
          }}
          folders={folders}
          label={I18n.t('Choose Target Folder')}
        />
      </ConditionalField>
      <Checkbox 
        layout={FieldLayout.componentThenLabel}
        label={I18n.t('Transmit Pedigree PDF to EMR')}
        value={questionnaire.statusTab.transmitToEMR}
        onChange={handleTransmitPedigreePdfToEmrChange}
        metadata={{
          description: 'transmit-pedigree-pdf-to-emr',
        }}
      />
    </div>
  );
};